/**
 * Scene timing model (spec 007): the one copy of dialog and sound timing.
 *
 * The editor canvas, the player, the timeline, `calculateSceneDuration` and
 * SceneRuntime all import from here; none of them declares its own timing
 * constants. Everything is pure and in milliseconds.
 */

import type { Dialog, DialogLine, KeyframeTrack, PlaybackSettings, Sound, SoundType, Transition } from '../@types';
import { applyEasing, toKeyframeEasing } from './interpolate';
import { backgroundChangesEnd } from './backgroundStates';

// ─── Constants ──────────────────────────────────────────────────────────────

/** The dialog panel fades in for this long before its first line. */
export const PANEL_FADE_IN_MS = 500;
/** The dialog panel fades out for this long after its last line. */
export const PANEL_FADE_OUT_MS = 500;
/** A `fade` line transition. */
export const FADE_TRANSITION_MS = 1000;
/** A `typing` line transition takes this long per character… */
export const TYPING_MS_PER_CHAR = 40;
/** …clamped to this range. */
export const TYPING_MIN_MS = 500;
export const TYPING_MAX_MS = 3000;
/** Empty text types as if it had this many characters. */
export const TYPING_EMPTY_CHARS = 10;
/** A line without a duration holds for this long (seconds). */
export const DEFAULT_LINE_DURATION_S = 3;

/** The pause after a voice (spec 041 D-5): a line fitted to its voice holds this much longer (seconds). */
export const VOICE_PAUSE_S = 0.5;
/** A line's duration range, seconds (spec 021's `RANGES.lineDuration`). */
const LINE_DURATION_MIN_S = 0.1;
const LINE_DURATION_MAX_S = 120;

/**
 * The seconds a line holds when fitted to a voice of `voiceMs` (spec 041
 * D-5): voice plus `VOICE_PAUSE_S` minus the line's in-transition `inMs`
 * (which plays while the voice speaks), rounded up to the tenth (with a 1e-9
 * tolerance, so 4.3 stays 4.3), clamped to [0.1, 120].
 */
export function fittedLineSeconds(voiceMs: number, inMs = 0): number {
  const raw = voiceMs / 1000 + VOICE_PAUSE_S - (Number.isFinite(inMs) && inMs > 0 ? inMs : 0) / 1000;
  const tenths = Math.ceil(raw * 10 - 1e-9) / 10;
  return Math.min(LINE_DURATION_MAX_S, Math.max(LINE_DURATION_MIN_S, Math.round(tenths * 10) / 10));
}

/** A usable voice length: finite and > 0 ms. */
export const isKnownVoiceLength = (voiceMs: number | undefined): voiceMs is number =>
  typeof voiceMs === 'number' && Number.isFinite(voiceMs) && voiceMs > 0;

/**
 * The one fit rule (spec 041 D-5, D-6): with `voiceMs` known and the line's
 * `durationSet` absent, the line with `duration` = `fittedLineSeconds(voiceMs,
 * inMs)`; otherwise the line unchanged (same object). The operations, the
 * editor store and the panel all call it with the line's `lineInMs`.
 */
export function fitLineToVoice(line: DialogLine, voiceMs?: number, inMs = 0): DialogLine {
  if (!isKnownVoiceLength(voiceMs) || line.durationSet) return line;
  const duration = fittedLineSeconds(voiceMs, inMs);
  return line.duration === duration ? line : { ...line, duration };
}

/** A line's in-transition length in its dialog (spec 041 D-5): the effective in-transition and its text. */
export function lineInMs(dialog: Dialog, line: DialogLine): number {
  return transitionDurationMs(lineTransitions(dialog, line).inTransition, line.text);
}

export const DEFAULT_IN_TRANSITION = 'typing';
export const DEFAULT_OUT_TRANSITION = 'fade';

// ─── Choice reveal ──────────────────────────────────────────────────────────

/** Whether a scene's exit transition plays: present, not `'none'`, duration > 0. */
export function isExitTransitionActive(exit: Transition | null | undefined): exit is Transition {
  return !!exit && exit.type !== 'none' && typeof exit.duration === 'number' && exit.duration > 0;
}

/**
 * A scene's hold point H (spec 015): the start of its active exit
 * transition's window, L − D (never below 0), else the play length L. A scene
 * with choices holds here until a selection or timeout plays the exit.
 */
export function exitWindowStart(sceneLengthMs: number, exitTransition?: Transition | null): number {
  const length = sceneLengthMs > 0 ? sceneLengthMs : 0;
  if (!isExitTransitionActive(exitTransition)) return length;
  return Math.max(0, length - exitTransition.duration);
}

/**
 * When the choice panel appears (spec 008): `showBeforeEnd` seconds before
 * the end of the scene's play length, clamped to [0, H], where H is the
 * scene's hold point `exitWindowStart(sceneLengthMs, exitTransition)` (spec
 * 015), so the panel never opens over an exit transition. `undefined`, NaN
 * and negative values of `showBeforeEnd` count as 0. The editor canvas, its
 * Preview, the previews and the runtime all pass the same play length and
 * exit transition. Re-exported from `runtime/StoryRuntime`.
 */
export function computeRevealTime(
  sceneLengthMs: number,
  showBeforeEndS: number | undefined | null,
  exitTransition?: Transition | null,
): number {
  const before = typeof showBeforeEndS === 'number' && Number.isFinite(showBeforeEndS) && showBeforeEndS > 0 ? showBeforeEndS : 0;
  const length = sceneLengthMs > 0 ? sceneLengthMs : 0;
  const hold = exitWindowStart(length, exitTransition);
  return Math.max(0, Math.min(hold, length - before * 1000));
}

// ─── Play length (spec 012) ─────────────────────────────────────────────────

/** The shortest play length of any scene. */
export const MIN_SCENE_LENGTH_MS = 1000;

/** The parts of a scene its play length depends on. */
export interface SceneLengthInput {
  duration?: number | null;
  dialogs?: Dialog[] | null;
  sounds?: Sound[] | null;
  keyframes?: KeyframeTrack[] | null;
  exitTransition?: Transition | null;
}

/**
 * A scene's play length (spec 012): the longer of its authored `duration`
 * (when > 0) and the computed end of its content, never less than 1 s. The
 * content end is the latest dialog end, sound end (a sound without `endTime`
 * does not lengthen the scene) and keyframe time, plus the exit transition.
 */
export function resolveSceneLength(scene: SceneLengthInput): number {
  let contentEnd = 0;
  for (const dialog of scene.dialogs ?? []) {
    contentEnd = Math.max(contentEnd, computeDialogTimeline(dialog).endMs);
  }
  for (const entry of computeSoundSchedule({ sounds: scene.sounds }, null, Infinity)) {
    if (Number.isFinite(entry.endMs)) contentEnd = Math.max(contentEnd, entry.endMs);
  }
  for (const track of scene.keyframes ?? []) {
    for (const kf of track.keyframes ?? []) {
      if (Number.isFinite(kf.time)) contentEnd = Math.max(contentEnd, kf.time);
    }
  }
  // A background state change plays to the end of its transition (spec 039).
  contentEnd = Math.max(contentEnd, backgroundChangesEnd(scene.keyframes));
  const exit = scene.exitTransition;
  if (exit && exit.type !== 'none' && exit.duration > 0) contentEnd += exit.duration;
  const authored = typeof scene.duration === 'number' && Number.isFinite(scene.duration) && scene.duration > 0 ? scene.duration : 0;
  return Math.max(authored, contentEnd, MIN_SCENE_LENGTH_MS);
}

/**
 * Eased progress (0–1) of a scene transition at `tMs` (spec 012). `enter`
 * runs from 0 over its duration (1 when there is none: fully entered); `exit`
 * ends at the play length (0 when there is none: not exiting). The ease name
 * maps to a keyframe easing (`toKeyframeEasing`).
 */
export function transitionProgress(
  transition: Transition | null | undefined,
  phase: 'enter' | 'exit',
  tMs: number,
  sceneLengthMs: number,
): number {
  const active = !!transition && transition.type !== 'none' && transition.duration > 0;
  if (!active) return phase === 'enter' ? 1 : 0;
  const start = phase === 'enter' ? 0 : sceneLengthMs - transition!.duration;
  const linear = Math.min(1, Math.max(0, (tMs - start) / transition!.duration));
  return applyEasing(linear, toKeyframeEasing(transition!.ease));
}

// ─── Dialog timeline ────────────────────────────────────────────────────────

/** Length of one line transition (`fade`, `typing` or `none`). */
export function transitionDurationMs(transition: string | undefined, text: string | undefined): number {
  if (transition === 'fade') return FADE_TRANSITION_MS;
  if (transition === 'typing') {
    const chars = text?.length || TYPING_EMPTY_CHARS;
    return Math.max(TYPING_MIN_MS, Math.min(TYPING_MAX_MS, chars * TYPING_MS_PER_CHAR));
  }
  return 0;
}

export interface DialogLineTiming {
  lineId: string;
  /** Absolute scene time the line starts (its in-transition begins). */
  startMs: number;
  /** In-transition ends, the text is fully shown. */
  inEndMs: number;
  /** Hold ends, the out-transition begins. */
  holdEndMs: number;
  /** Out-transition ends. */
  endMs: number;
}

export interface DialogTimeline {
  dialogId: string;
  /** Absolute scene time the panel starts fading in. */
  startMs: number;
  /** Absolute time the content (first line slot) starts: start + panel fade in. */
  contentStartMs: number;
  /** Absolute time the last line ends; the panel fades out from here. */
  contentEndMs: number;
  /** Panel fade in + lines + panel fade out. */
  lengthMs: number;
  /** startMs + lengthMs. */
  endMs: number;
  lines: DialogLineTiming[];
}

/** The line's in/out transitions after the dialog-level defaults apply. */
export function lineTransitions(dialog: Dialog, line: DialogLine): { inTransition: string; outTransition: string } {
  return {
    inTransition: line.inTransition || dialog.inTransition || DEFAULT_IN_TRANSITION,
    outTransition: line.outTransition || dialog.outTransition || DEFAULT_OUT_TRANSITION,
  };
}

/**
 * The dialog's timing. A line with `startTime` starts there (relative to the
 * content start); a line without one starts where the previous line ends.
 * `transitionDuration` fields are reserved and ignored.
 */
export function computeDialogTimeline(dialog: Dialog): DialogTimeline {
  const startMs = Math.max(0, dialog.startTime || 0);
  const contentStartMs = startMs + PANEL_FADE_IN_MS;
  const lines: DialogLineTiming[] = [];
  let cursor = 0;
  let linesEnd = 0;
  for (const line of dialog.lines ?? []) {
    const { inTransition, outTransition } = lineTransitions(dialog, line);
    const inMs = transitionDurationMs(inTransition, line.text);
    const outMs = transitionDurationMs(outTransition, line.text);
    const holdMs = (line.duration || DEFAULT_LINE_DURATION_S) * 1000;
    const rel = line.startTime ?? cursor;
    const relEnd = rel + inMs + holdMs + outMs;
    lines.push({
      lineId: line.id,
      startMs: contentStartMs + rel,
      inEndMs: contentStartMs + rel + inMs,
      holdEndMs: contentStartMs + rel + inMs + holdMs,
      endMs: contentStartMs + relEnd,
    });
    if (relEnd > linesEnd) linesEnd = relEnd;
    if (line.startTime === undefined) cursor = relEnd;
  }
  const lengthMs = PANEL_FADE_IN_MS + linesEnd + PANEL_FADE_OUT_MS;
  return {
    dialogId: dialog.id,
    startMs,
    contentStartMs,
    contentEndMs: contentStartMs + linesEnd,
    lengthMs,
    endMs: startMs + lengthMs,
    lines,
  };
}

export type DialogLinePhase = 'in' | 'visible' | 'out' | 'hidden';
export type DialogPanelPhase = 'fade-in' | 'visible' | 'fade-out' | 'hidden';

/** What a dialog shows at one moment: every renderer draws from this. */
export interface DialogFrame {
  line: DialogLine | null;
  /** 0–1 through the line's current phase. */
  progress: number;
  phase: DialogLinePhase;
  panelPhase: DialogPanelPhase;
  /** Panel opacity, 0–1. */
  panelProgress: number;
}

const HIDDEN: DialogFrame = { line: null, progress: 0, phase: 'hidden', panelPhase: 'hidden', panelProgress: 0 };

/**
 * The dialog's state at `timeMs` (absolute scene time). Between lines with
 * explicit start times the panel stays visible with no text.
 */
export function dialogFrameAt(dialog: Dialog, timeMs: number, timeline = computeDialogTimeline(dialog)): DialogFrame {
  if (timeMs < timeline.startMs || timeMs >= timeline.endMs) return HIDDEN;
  if (timeMs < timeline.contentStartMs) {
    return { ...HIDDEN, panelPhase: 'fade-in', panelProgress: (timeMs - timeline.startMs) / PANEL_FADE_IN_MS };
  }
  if (timeMs >= timeline.contentEndMs) {
    return { ...HIDDEN, panelPhase: 'fade-out', panelProgress: 1 - (timeMs - timeline.contentEndMs) / PANEL_FADE_OUT_MS };
  }
  for (let i = 0; i < timeline.lines.length; i++) {
    const t = timeline.lines[i];
    if (timeMs < t.startMs || timeMs >= t.endMs) continue;
    const line = dialog.lines[i];
    if (timeMs < t.inEndMs) {
      return { line, phase: 'in', progress: (timeMs - t.startMs) / (t.inEndMs - t.startMs), panelPhase: 'visible', panelProgress: 1 };
    }
    if (timeMs < t.holdEndMs) {
      return { line, phase: 'visible', progress: 1, panelPhase: 'visible', panelProgress: 1 };
    }
    const outMs = t.endMs - t.holdEndMs;
    return { line, phase: 'out', progress: outMs > 0 ? (timeMs - t.holdEndMs) / outMs : 1, panelPhase: 'visible', panelProgress: 1 };
  }
  return { ...HIDDEN, panelPhase: 'visible', panelProgress: 1 };
}

// ─── Sounds and voice cues ──────────────────────────────────────────────────

export interface SoundScheduleEntry {
  soundId: string;
  mediaId: number;
  startMs: number;
  endMs: number;
  loop: boolean;
  gain: number;
  pan: number;
  fadeInMs: number;
  fadeOutMs: number;
}

export interface DialogAudioCue {
  dialogId: string;
  lineId: string;
  mediaId: number;
  startMs: number;
  endMs: number;
}

type VolumeSettings = Pick<PlaybackSettings, 'defaultBgmVolume' | 'defaultSfxVolume' | 'defaultVoiceVolume'>;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const finite = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);

/** The default volume for a sound type (1 when the settings lack it). */
export function typeVolume(type: SoundType | undefined, settings: Partial<VolumeSettings> | null | undefined): number {
  const v = type === 'bgm' ? settings?.defaultBgmVolume
    : type === 'voice' ? settings?.defaultVoiceVolume
      : settings?.defaultSfxVolume;
  return finite(v, 1);
}

/**
 * When and how each sound plays. Sounds without media are skipped; a sound
 * whose window is empty is dropped. A sound without `endTime` plays to the
 * scene end; no sound plays past it.
 */
export function computeSoundSchedule(
  scene: { sounds?: Sound[] | null },
  playbackSettings: Partial<VolumeSettings> | null | undefined,
  sceneDurationMs: number,
): SoundScheduleEntry[] {
  const out: SoundScheduleEntry[] = [];
  for (const sound of scene.sounds ?? []) {
    if (!sound || !(sound.mediaId > 0)) continue;
    const startMs = Math.max(0, finite(sound.startTime, 0));
    const endMs = Math.min(finite(sound.endTime, sceneDurationMs), sceneDurationMs);
    if (startMs >= endMs) continue;
    const half = (endMs - startMs) / 2;
    out.push({
      soundId: sound.id,
      mediaId: sound.mediaId,
      startMs,
      endMs,
      loop: !!sound.loop,
      gain: clamp(finite(sound.volume, 1) * typeVolume(sound.type, playbackSettings), 0, 1),
      pan: clamp(finite(sound.pan, 0), -1, 1),
      fadeInMs: clamp(finite(sound.fadeIn, 0), 0, half),
      fadeOutMs: clamp(finite(sound.fadeOut, 0), 0, half),
    });
  }
  return out;
}

/** One cue per dialog line with audio, starting with the line's in-transition. */
export function computeDialogAudioCues(scene: { dialogs?: Dialog[] | null }): DialogAudioCue[] {
  const out: DialogAudioCue[] = [];
  for (const dialog of scene.dialogs ?? []) {
    const timeline = computeDialogTimeline(dialog);
    dialog.lines.forEach((line, i) => {
      if (!line.audioId || !(line.audioId > 0)) return;
      const t = timeline.lines[i];
      out.push({ dialogId: dialog.id, lineId: line.id, mediaId: line.audioId, startMs: t.startMs, endMs: t.endMs });
    });
  }
  return out;
}

/** A cue the audio driver plays: a sound or a dialog voice line. */
export interface AudioCue {
  /** Unique per cue: `sound:<id>` or `voice:<dialogId>:<lineId>`. */
  key: string;
  mediaId: number;
  startMs: number;
  endMs: number;
  loop: boolean;
  gain: number;
  pan: number;
  fadeInMs: number;
  fadeOutMs: number;
}

/** Every audio cue of a scene: the sound schedule plus voice cues at the voice volume. */
export function computeSceneAudioCues(
  scene: { sounds?: Sound[] | null; dialogs?: Dialog[] | null },
  playbackSettings: Partial<VolumeSettings> | null | undefined,
  sceneDurationMs: number,
): AudioCue[] {
  const voiceGain = clamp(typeVolume('voice', playbackSettings), 0, 1);
  return [
    ...computeSoundSchedule(scene, playbackSettings, sceneDurationMs).map((s): AudioCue => ({
      key: `sound:${s.soundId}`,
      mediaId: s.mediaId,
      startMs: s.startMs,
      endMs: s.endMs,
      loop: s.loop,
      gain: s.gain,
      pan: s.pan,
      fadeInMs: s.fadeInMs,
      fadeOutMs: s.fadeOutMs,
    })),
    ...computeDialogAudioCues(scene).map((c): AudioCue => ({
      key: `voice:${c.dialogId}:${c.lineId}`,
      mediaId: c.mediaId,
      startMs: c.startMs,
      endMs: c.endMs,
      loop: false,
      gain: voiceGain,
      pan: 0,
      fadeInMs: 0,
      fadeOutMs: 0,
    })),
  ];
}

/** The cue's gain at `timeMs` with its fade-in/fade-out envelope applied. */
export function cueGainAt(cue: AudioCue, timeMs: number): number {
  if (timeMs < cue.startMs || timeMs >= cue.endMs) return 0;
  let env = 1;
  if (cue.fadeInMs > 0) env = Math.min(env, (timeMs - cue.startMs) / cue.fadeInMs);
  if (cue.fadeOutMs > 0) env = Math.min(env, (cue.endMs - timeMs) / cue.fadeOutMs);
  return cue.gain * clamp(env, 0, 1);
}

// ─── Panel input helpers ────────────────────────────────────────────────────

/**
 * A seconds field that may be empty (spec 007): empty or unparsable text is
 * `undefined`, a number is milliseconds (never negative).
 */
export function parseOptionalSeconds(text: string): number | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return undefined;
  return Math.max(0, Math.round(n * 1000));
}

/** Milliseconds shown as seconds in a field; `undefined` shows empty. */
export function formatOptionalSeconds(ms: number | undefined): string {
  return ms === undefined ? '' : String(Math.round(ms) / 1000);
}

/** The shortest sound window the panel and the timeline allow (ms). */
export const MIN_SOUND_WINDOW_MS = 100;

export type SoundTiming = Pick<Sound, 'startTime' | 'endTime' | 'fadeIn' | 'fadeOut'>;

/**
 * A sound's timing after an edit, clamped (spec 007): Start ≥ 0; End empty or
 * later than Start (an End at or before Start moves to Start + 0.1 s); fades
 * ≥ 0 and each no longer than half the play length (End, or the scene end
 * when End is empty).
 */
export function clampSoundTiming(current: SoundTiming, patch: Partial<SoundTiming>, sceneDurationMs: number): SoundTiming {
  const merged: SoundTiming = { ...current, ...patch };
  const startTime = Math.max(0, Math.round(finite(merged.startTime, 0)));
  let endTime = merged.endTime === undefined || !Number.isFinite(merged.endTime) ? undefined : Math.round(merged.endTime);
  if (endTime !== undefined && endTime <= startTime) endTime = startTime + MIN_SOUND_WINDOW_MS;
  const playLen = (endTime ?? sceneDurationMs) - startTime;
  const fadeCap = playLen > 0 ? playLen / 2 : Infinity;
  const fade = (v: number | undefined) => Math.round(clamp(finite(v, 0), 0, fadeCap));
  return { startTime, endTime, fadeIn: fade(merged.fadeIn), fadeOut: fade(merged.fadeOut) };
}

/** Sound fields that follow the type until the author edits them (spec 007). */
export const SOUND_TYPE_DEFAULTS: Record<SoundType, { volume: number; loop: boolean; fadeIn: number; fadeOut: number }> = {
  bgm: { volume: 0.5, loop: true, fadeIn: 1000, fadeOut: 1000 },
  sfx: { volume: 1, loop: false, fadeIn: 0, fadeOut: 0 },
  voice: { volume: 1, loop: false, fadeIn: 0, fadeOut: 0 },
};

/**
 * A sound changing type: each of volume, loop and the fades that still holds
 * the old type's default takes the new type's default; an edited one stays.
 */
export function soundTypeChange(sound: Pick<Sound, 'type' | 'volume' | 'loop' | 'fadeIn' | 'fadeOut'>, type: SoundType): Partial<Sound> {
  const from = SOUND_TYPE_DEFAULTS[sound.type] ?? SOUND_TYPE_DEFAULTS.sfx;
  const to = SOUND_TYPE_DEFAULTS[type];
  const updates: Partial<Sound> = { type };
  if (sound.volume === from.volume) updates.volume = to.volume;
  if (sound.loop === from.loop) updates.loop = to.loop;
  if ((sound.fadeIn ?? 0) === from.fadeIn) updates.fadeIn = to.fadeIn;
  if ((sound.fadeOut ?? 0) === from.fadeOut) updates.fadeOut = to.fadeOut;
  return updates;
}

/**
 * The lines after one line's Start (s) changes (spec 007): a number sets
 * `startTime`; `undefined` removes it, so the line runs back to back again.
 */
export function withLineStartTime(lines: DialogLine[], lineId: string, startTime: number | undefined): DialogLine[] {
  return lines.map((line) => {
    if (line.id !== lineId) return line;
    const { startTime: _previous, ...rest } = line;
    return startTime === undefined ? rest : { ...rest, startTime };
  });
}
